<template>
	<div>
		<!-- Sentinel to know when to show save form bar as fixed/static -->
		<div v-intersect="onIntersect"></div>

		<template v-if="!isIntersecting && newForms">
			<v-divider></v-divider>
		</template>

		<component
				:is="!isIntersecting || !sticky ? 'ui-box' : 'div'"
				:class="{
					'fixed save-form-wrapper': isIntersecting && sticky,
					'is-dirty': isDirty
				}"
				:new-forms="newForms"
				innerClasses="align-center d-flex"
				v-resize="getLeftPosition"
		>
			<v-container class="pa-0">
				<v-row>
					<v-col>
						<v-progress-linear
								v-if="isSaving"
								:class="{
									'w-100': true,
									'save-progress': !isIntersecting || !sticky,
									'save-progress-fixed': isIntersecting && sticky
								}"
								color="secondary"
								height="2"
								indeterminate
						></v-progress-linear>

						<div
								class="align-center d-flex"
								:class="{ 'px-4 py-4': isIntersecting && sticky }"
						>
							<div
									v-if="$vuetify.breakpoint.smAndUp"
									class="flex-grow-1 me-0 me-sm-4 me-md-8 text-body-2"
							>
								<template v-if="isValid">
									<span class="success--text text-caption text-md-body-2">
										{{ isValidText }}
									</span>
								</template>
								<template v-else-if="isSaving">
									<span class="secondary--text text-caption text-md-body-2">	
										Ahhh... patience, while we save your precious data.
									</span>
								</template>
								<template v-else-if="!isValid && isDirty">
									<span class="error--text text-caption text-md-body-2">	
										To save, complete the form or fix indicated errors.
									</span>
								</template>
								<template v-else-if="isDirty">
									<span class="warning--text text-caption text-md-body-2">	
										You have unsaved changes.
									</span>
								</template>
							</div>
							<v-btn
									class="rounded-lg"
									color="grey darken-1"
									depressed
									:loading="isSaving"
									text
									@click="cancelButtonClicked"
							>
								{{ cancelText }}
							</v-btn>
							<v-divider
									v-if="prevText && $vuetify.breakpoint.smAndUp"
									class="me-2 me-md-4 ms-4 ms-md-8"
									vertical>
							</v-divider>
							<v-spacer v-if="$vuetify.breakpoint.xsOnly"></v-spacer>
							<v-btn
									v-if="prevText"
									class="ms-2 ms-md-4 rounded-lg"
									color="secondary"
									depressed
									:loading="isSaving"
									@click="$emit('previous-action', true)"
							>
								{{ prevText }}
							</v-btn>
							<v-btn
									v-if="archiveText"
									class="ms-2 ms-md-4 rounded-lg"
									color="#d26a5c"
									depressed
									:loading="isSaving"
									:style="{ backgroundColor: '#d26a5c', color: 'white' }"
									@click="$emit('archive-action', true)"
							>
								{{ archiveText }}
							</v-btn>

							<v-btn
									class="ms-2 ms-md-4 rounded-lg"
									color="primary"
									depressed
									:disabled="!isValid"
									:loading="isSaving"
									@click="saveButtonClicked"
							>
								{{ saveText }}
							</v-btn>
						</div>
					</v-col>
				</v-row>
			</v-container>
		</component>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
	name: "SaveForm",
	props: {
		archiveText: {
			type: String,
			default: ''
		},
		additionalValidations: {
			type: Array,
			default: () => []
		},
		cancelText: {
			type: String,
			default: 'Cancel'
		},
		refs: {
			type: Object,
			required: true
		},
		isSaving: {
			type: Boolean,
			default: false
		},
		isValidText: {
			type: String,
			default: 'The form is valid and ready to save.'
		},
		newForms: Boolean,
		prevText: {
			type: String,
			default: ''
		},
		resetForm: {
			type: Boolean,
			default: false
		},
		saveText: {
			type: String,
			default: 'Save'
		},
		sticky: Boolean
	},
	data() {
		return {
			boxClass: '',
			fixed: undefined,
			forms: [],
			isIntersecting: undefined,
			left: 0
		}
	},
	computed: {
		...mapGetters('formState', { externalIsDirty: 'getIsDirty' }),
		isDirty() {
			return this.forms.some(form => this.isFormDirty(form)) || this.externalIsDirty;
		},
		isValid() {
			return this.forms.every(form => form?.$props?.value || false)
					&& this.additionalValidations.every(Boolean);
		}
	},
	methods: {
		...mapActions('formState', ['setDirtyState']),
		cancelButtonClicked() {
			this.resetDirty();
			this.$emit('cancel-action', true)
		},
		getLeftPosition() {
			if(!this.sticky) {
				return;
			}

			let newLeft = 0;
			const secondaryNavEl = document.getElementById('secondary-nav');
			const sideNavEl = document.getElementById('side-nav');
			const sideNavState = {
				hover:
						this.$vuetify.breakpoint.smOnly ||
						this.$vuetify.breakpoint.mdOnly ||
						(!this.$vuetify.breakpoint.xs && !!secondaryNavEl),
				temporary: this.$vuetify.breakpoint.xs,
				permanent: this.$vuetify.breakpoint.smAndUp
			};
			const secondaryNavState = {
				temporary: this.$vuetify.breakpoint.mdAndDown,
				permanent: this.$vuetify.breakpoint.lgAndUp
			};

			if(!!sideNavEl && sideNavState.permanent && !sideNavState.hover) newLeft += 256;
			if(!!sideNavEl && sideNavState.hover) newLeft += 56;
			if(!!secondaryNavEl && secondaryNavState.permanent) newLeft += 300;

			this.left = !this.isIntersecting ? 0 : newLeft;
		},
		isCountryCode(input) {
			const inputValue = input.$props?.value;
			return this.isObject(inputValue) && 'dialCode' in inputValue;
		},
		isFormDirty(formComponent) {
			if(!formComponent) {
				return false;
			}
			return formComponent.inputs.some(input => {
				if(this.isCountryCode(input) || this.isUiOnly(input)) {
					return false;
				}
				const isSwitchComponent = this.isSwitch(input);
				return (isSwitchComponent && input.hasFocused) ||
						(!isSwitchComponent && input.isDirty && input.hasInput);
			});
		},
		isObject(value) {
			return value !== null && typeof value === 'object' && !Array.isArray(value);
		},
		isSwitch(input) {
			return input?.attrs?.role === 'switch';
		},
		isUiOnly(input) {
			return input.$attrs.hasOwnProperty('ui-only') || input.hasOwnProperty('uiOnly');
		},
		onIntersect(entries, observer, isIntersecting) {
			this.fixed = isIntersecting;
			this.isIntersecting = !isIntersecting;
		},
		resetDirty() {
			if(this.resetForm) {
				this.setDirtyState(false);
				this.$nextTick(() => {
					this.forms.forEach(form => {
						if(form) {
							const filteredComponents = form.inputs.filter(i => !this.isCountryCode(i));
							filteredComponents.forEach(input => input.resetValidation());
						}
					})
				});
			}
		},
		saveButtonClicked() {
			this.resetDirty();
			this.$emit('save-action', true)
		}
	},
	mounted() {
		this.getLeftPosition();

		this.forms = Object.keys(this.refs)
				.filter(refKey => {
					return this.refs[refKey] !== null
							&& this.refs[refKey] !== undefined
							&& this.refs[refKey] !== []
							&& refKey.endsWith('Form');
				})
				.map(refKey => {
					if(Array.isArray(this.refs[refKey])) {
						return this.refs[refKey][0];
					}
					return this.refs[refKey];
				});
	},
	watch: {
		isDirty(newIsDirty) {
			this.setDirtyState(newIsDirty);
			if(newIsDirty)
				document.body.classList.add('has-dirty-form');
			else
				document.body.classList.remove('has-dirty-form');
		}
	}
};
</script>

<style lang="scss" scoped>
.save-form-wrapper {
	background: white;
	border: solid 1px #c9f6b1 !important;
	border-radius: 1rem;
	height: 77px;
	position: static;
	transition: left 300ms ease-in-out;

	&.fixed {
		border: none !important;
		border-radius: 0;
		border-top: solid 1px #e4e4e4 !important;
		bottom: 0;
		box-shadow: 0 0 16px 16px rgba(255, 255, 255, 0);
		position: fixed;
		right: 0;
		transition: box-shadow 0.3s ease-in-out;

		&.is-dirty {
			border-top: none !important;
			box-shadow: 0 0 16px 16px rgba(255, 127, 0, 0.12);
		}
	}
}

::v-deep .v-progress-linear {
	&.save-progress {
		margin-bottom: 14px !important;
		margin-top: -16px !important;
		position: relative;
	}

	&.save-progress-fixed {
		margin-bottom: -2px !important;
		margin-top: 0 !important;
	}

	&[class*="save-progress"] {

		&::after,
		&::before {
			bottom: 0;
			content: '';
			height: 2px;
			position: absolute;
			width: 20%;
			z-index: 1;
		}

		&::before {
			background: linear-gradient(to right, #FFF, rgba(255, 255, 255, 0));
			left: 0;
		}

		&::after {
			background: linear-gradient(to left, #FFF, rgba(255, 255, 255, 0));
			right: 0;
		}
	}
}
</style>